import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, DataTable, Pagination, Select, StatusBadge, TextInput, useLocale, type DataTableColumn } from '@ministry/ui/internal';

import { listUsers, type AdminUserListRow, type UserAccountStatus } from '../../lib/admin-users.js';
import { accountStatusEntry } from '../../lib/admin-lifecycle.js';
import { ApiError } from '../../lib/api-client.js';
import { useT } from '../../i18n/strings.js';
import { PageHeader } from '../../components/PageHeader.js';
import { ForbiddenState } from '../../components/ForbiddenState.js';
import { roleLabel } from '../../lib/roles.js';
import { ADMIN_ROUTES } from './routes.js';

/**
 * SCREEN_SPECS.md I19 (list half) — staff directory with server-side search, status
 * filter and offset pagination (`GET /users`, API_CONTRACT §13.10). Loading/error come
 * for free from `DataTable` (same pattern `AvailabilityAdminPage` already uses);
 * `FORBIDDEN_*` short-circuits to the shared `ForbiddenState` before the table renders
 * at all, matching that same screen's own split.
 */
export function UserDirectoryPage(): React.JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const [page, setPage] = React.useState(1);
  const [qInput, setQInput] = React.useState('');
  const [q, setQ] = React.useState('');
  const [status, setStatus] = React.useState<UserAccountStatus | ''>('');

  const query = useQuery({
    queryKey: ['admin-users', page, q, status],
    queryFn: ({ signal }) =>
      listUsers({ page, pageSize: 25, q: q || undefined, status: status || undefined }, signal),
  });

  if (query.isError && query.error instanceof ApiError && query.error.code.startsWith('FORBIDDEN')) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={t('admin.users.title')} />
        <ForbiddenState error={query.error} />
      </div>
    );
  }

  const rows = query.data?.rows ?? [];
  const filterActive = q !== '' || status !== '';

  const columns: DataTableColumn<AdminUserListRow>[] = [
    {
      key: 'fullName',
      header: t('admin.users.column.name'),
      visibleFrom: 'always',
      cardSlot: 'title',
      grow: true,
      render: (row) => (
        <Link to={ADMIN_ROUTES.userDetail(row.id)} className="font-semibold text-text-link underline-offset-2 hover:underline">
          {row.fullName}
        </Link>
      ),
    },
    {
      key: 'username',
      header: t('admin.users.column.username'),
      visibleFrom: 'md',
      cardSlot: 'line2',
      render: (row) => row.username,
    },
    {
      key: 'orgUnit',
      header: t('admin.users.column.orgUnit'),
      visibleFrom: 'md',
      cardSlot: 'line3',
      render: (row) => (row.orgUnit ? (locale === 'ne' ? row.orgUnit.nameNe : row.orgUnit.nameEn) : '—'),
    },
    {
      key: 'roles',
      header: t('admin.users.column.roles'),
      visibleFrom: 'lg',
      cardSlot: 'line4',
      render: (row) =>
        row.roleCodes.length > 0 ? row.roleCodes.map((code) => roleLabel(code, locale)).join(', ') : t('admin.users.noRoles'),
    },
    {
      key: 'status',
      header: t('admin.users.column.status'),
      visibleFrom: 'always',
      cardSlot: 'status',
      render: (row) => <StatusBadge entry={accountStatusEntry(row.status)} />,
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('admin.users.title')}
        description={t('admin.users.description')}
        actions={
          <Button variant="primary" onClick={() => navigate(ADMIN_ROUTES.userNew)}>
            {t('admin.users.newUser')}
          </Button>
        }
      />

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
          setQ(qInput.trim());
        }}
      >
        <div className="w-64">
          <TextInput
            id="admin-users-search"
            label={t('admin.users.search.label')}
            placeholder={t('admin.users.search.placeholder')}
            value={qInput}
            onChange={(event) => setQInput(event.target.value)}
          />
        </div>
        <div className="w-48">
          <Select
            id="admin-users-status"
            label={t('admin.users.filter.status.label')}
            value={status || 'all'}
            onValueChange={(value) => {
              setPage(1);
              setStatus(value === 'all' ? '' : (value as UserAccountStatus));
            }}
            options={[
              { value: 'all', label: t('admin.users.filter.status.all') },
              { value: 'active', label: t('admin.users.filter.status.active') },
              { value: 'suspended', label: t('admin.users.filter.status.suspended') },
              { value: 'deactivated', label: t('admin.users.filter.status.deactivated') },
            ]}
          />
        </div>
        <Button type="submit" variant="secondary">
          {t('admin.users.search.submit')}
        </Button>
      </form>

      <DataTable
        caption={t('admin.users.caption')}
        columns={columns}
        rows={rows}
        getRowId={(row) => row.id}
        loading={query.isLoading}
        error={query.isError}
        onRetry={() => query.refetch()}
        onRowClick={(row) => navigate(ADMIN_ROUTES.userDetail(row.id))}
        emptyTitle={filterActive ? t('admin.users.emptyFiltered.title') : t('admin.users.empty.title')}
        emptyBody={filterActive ? t('admin.users.emptyFiltered.body') : t('admin.users.empty.body')}
      />

      {query.data && query.data.page.totalPages > 1 ? (
        <Pagination page={page} pageCount={query.data.page.totalPages} onPageChange={setPage} loading={query.isFetching} />
      ) : null}
    </div>
  );
}
